import React, { useRef, useEffect, useState } from 'react';
import { FiPlay, FiPause, FiVolume2, FiVolumeX, FiSkipBack, FiSkipForward } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const AudioPlayer = ({ currentEpisode, isPlaying, setIsPlaying }) => {
  const audioRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);

  useEffect(() => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.play().catch(e => console.log('Playback error', e));
      } else {
        audioRef.current.pause();
      }
    }
  }, [isPlaying, currentEpisode]);

  const togglePlay = () => {
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const current = audioRef.current.currentTime;
      const total = audioRef.current.duration;
      setProgress((current / total) * 100);
      setDuration(total);
    }
  };

  const handleSeek = (e) => {
    const seekTime = (e.target.value / 100) * duration;
    audioRef.current.currentTime = seekTime;
    setProgress(e.target.value);
  };

  const skip = (seconds) => {
    if (audioRef.current) {
      audioRef.current.currentTime += seconds;
    }
  };

  const formatTime = (time) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-100 shadow-[0_-10px_30px_-5px_rgba(0,0,0,0.1)] px-4 py-4 z-50">
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center gap-4">
        <audio 
          ref={audioRef} 
          src={currentEpisode.url} 
          onTimeUpdate={handleTimeUpdate} 
          onEnded={() => setIsPlaying(false)} 
        />
        
        {/* Info */}
        <div className="w-full md:w-1/4 flex items-center gap-3">
          <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100 hidden md:block">
            <img 
              src={currentEpisode.image || "https://media-manager-c.questera.ai/greta-media/27e565ca62f331bb3d3ca67ccd492f06cd2fa92619ed72ab1d77f8d98c6e5b4cc6657f2d778fcddfd847ef506ea1c4d1/images/aW1hZ2UvcG5n/0902e344c6041ca9f56ff9415cbb69cb.png"} 
              className="w-full h-full object-cover"
              alt="" 
            />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] text-[#F28423] font-bold uppercase tracking-widest leading-none mb-1">SPELAS NU</p>
            <h4 className="font-bold text-gray-900 text-sm truncate">{currentEpisode.title}</h4>
          </div>
        </div>

        {/* Controls */}
        <div className="flex flex-col items-center w-full md:w-2/4 gap-2">
          <div className="flex items-center gap-8">
            <button onClick={() => skip(-15)} className="text-gray-400 hover:text-[#F28423] transition-colors">
              <SafeIcon icon={FiSkipBack} className="w-6 h-6" />
            </button>
            <button 
              onClick={togglePlay} 
              className="w-12 h-12 rounded-full bg-[#F28423] text-white flex items-center justify-center hover:bg-orange-600 transition-all shadow-lg active:scale-95"
            >
              <SafeIcon icon={isPlaying ? FiPause : FiPlay} className="w-6 h-6 ml-0.5" />
            </button>
            <button onClick={() => skip(15)} className="text-gray-400 hover:text-[#F28423] transition-colors">
              <SafeIcon icon={FiSkipForward} className="w-6 h-6" />
            </button>
          </div>
          
          <div className="w-full flex items-center gap-3 text-xs text-gray-500 font-bold">
            <span className="w-10 text-right">{formatTime(audioRef.current?.currentTime)}</span>
            <div className="flex-grow relative group h-4 flex items-center">
              <input 
                type="range" 
                min="0" 
                max="100" 
                value={progress || 0} 
                onChange={handleSeek} 
                className="w-full h-1 bg-gray-100 rounded-lg appearance-none cursor-pointer accent-[#F28423] group-hover:h-1.5 transition-all" 
              />
            </div>
            <span className="w-10">{formatTime(duration)}</span>
          </div>
        </div>

        {/* Volume */}
        <div className="w-full md:w-1/4 hidden md:flex items-center justify-end gap-3">
          <button 
            onClick={() => {
              audioRef.current.muted = !isMuted;
              setIsMuted(!isMuted);
            }} 
            className="text-gray-400 hover:text-gray-900"
          >
            <SafeIcon icon={isMuted ? FiVolumeX : FiVolume2} className="w-5 h-5" />
          </button>
          <div className="w-24 h-1 bg-gray-100 rounded-full overflow-hidden">
             <div className="h-full bg-gray-400 w-2/3"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudioPlayer;